import { supabase } from '@/lib/supabase';
import { mapSupplierFromDb, type Supplier, type SupplierOption } from '../types';

/**
 * Fetch all suppliers for a business
 */
export async function fetchSuppliers(businessId: string): Promise<Supplier[]> {
  const { data, error } = await supabase
    .from('suppliers')
    .select('*')
    .eq('business_id', businessId)
    .order('name', { ascending: true });

  if (error) {
    console.error('Error fetching suppliers:', error);
    throw error;
  }

  return (data || []).map(mapSupplierFromDb);
}

/**
 * Fetch suppliers as dropdown options
 */
export async function fetchSupplierOptions(businessId: string): Promise<SupplierOption[]> {
  const { data, error } = await supabase
    .from('suppliers')
    .select('id, name')
    .eq('business_id', businessId)
    .order('name', { ascending: true });

  if (error) {
    console.error('Error fetching supplier options:', error);
    throw error;
  }

  return (data || []).map((row) => ({
    id: row.id,
    name: row.name,
  }));
}

/**
 * Create a new supplier with just a name
 */
export async function createSupplier(businessId: string, name: string): Promise<Supplier> {
  const { data, error } = await supabase
    .from('suppliers')
    .insert({
      business_id: businessId,
      name: name.trim(),
    })
    .select()
    .single();

  if (error) {
    console.error('Error creating supplier:', error);
    throw error;
  }

  return mapSupplierFromDb(data);
}

/**
 * Check if a supplier name already exists for the business
 */
export async function supplierNameExists(businessId: string, name: string): Promise<boolean> {
  const { data, error } = await supabase
    .from('suppliers')
    .select('id')
    .eq('business_id', businessId)
    .ilike('name', name.trim())
    .limit(1);

  if (error) {
    console.error('Error checking supplier name:', error);
    throw error;
  }

  return (data?.length ?? 0) > 0;
}